import { useState, useMemo, useCallback } from "react";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config";

const T = {
  blue: "#2563eb", blueL: "#eff6ff", blueB: "#bfdbfe",
  green: "#15803d", greenL: "#f0fdf4", greenB: "#bbf7d0",
  amber: "#b45309", amberL: "#fffbeb", amberB: "#fde68a",
  red: "#be123c", redL: "#fff1f2", redB: "#fecdd3",
  ink: "#0f172a", ink2: "#334155", ink3: "#64748b", ink4: "#94a3b8",
  rule: "#e2e8f0", bg: "#f8fafc", white: "#fff",
};

const Spin = ({ size = 12, color = T.blue }) => (
  <span style={{ display: "inline-block", width: size, height: size, border: `2px solid ${color}22`, borderTopColor: color, borderRadius: "50%", animation: "qod-spin .6s linear infinite" }} />
);

/**
 * Right-hand drawer listing one owner's qualitative questions — the
 * qualitative counterpart of BulkOwnerAssign/OwnerAssignedKpisDrawer, with the
 * location dimension removed: qualitative assignments are (question, user)
 * pairs, so each question is itself the removable row.
 *
 * mode="unassigned": read-only list of the selected questions this owner is
 *   NOT yet assigned to ("Needs owner").
 * mode="assignments": one checkable + removable row per question this owner
 *   IS assigned to; removals go through the bulk qualitative-data/unassign
 *   endpoint in a single call scoped to this owner.
 *
 * Props:
 *   scopeId              {string|number} — the owner's userId
 *   scopeLabel           {string}        — the owner's display name
 *   qIds                 {number[]}      — question ids listed in this drawer
 *   mode                 {"unassigned"|"assignments"}
 *   questionTitles       {object}        — { questionId: title }
 *   financialYearId      {number}
 *   onClose              {function}
 *   onRefreshAssignments {function}      — refetch assignments after a removal
 */
const QualitativeOwnerAssignedDrawer = ({
  scopeId,
  scopeLabel,
  qIds,
  mode,
  questionTitles,
  financialYearId,
  onClose,
  onRefreshAssignments,
}) => {
  const isReadOnly = mode === "unassigned";
  const [search, setSearch] = useState("");
  const [removalError, setRemovalError] = useState("");
  const [removingIds, setRemovingIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const filteredQIds = useMemo(() => qIds.filter((qid) => {
    const title = (questionTitles[qid] || `Question #${qid}`).toLowerCase();
    return !search || title.includes(search.toLowerCase());
  }), [qIds, questionTitles, search]);

  const allSelected = filteredQIds.length > 0 && filteredQIds.every((qid) => selectedIds.has(qid));

  const toggleSelect = (qid) => {
    setSelectedIds((prev) => { const n = new Set(prev); n.has(qid) ? n.delete(qid) : n.add(qid); return n; });
  };
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(filteredQIds));
  };

  /**
   * Removes this owner from the given questions with a single bulk unassign
   * call — one entry per question, all scoped to scopeId.
   */
  const performRemovals = useCallback(async (ids) => {
    if (!ids.length) return;
    setRemovalError("");
    setRemovingIds(new Set(ids));
    setBulkBusy(ids.length > 1);
    try {
      const { isSuccess } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}qualitative-data/unassign`, {},
        {
          financialYearId,
          unassignments: ids.map((qid) => ({ questionId: Number(qid), userIds: [Number(scopeId)] })),
        },
        "POST"
      );
      if (!isSuccess) {
        setRemovalError("Failed to remove assignment(s).");
        return;
      }
      await onRefreshAssignments?.();
      setSelectedIds((prev) => { const n = new Set(prev); ids.forEach((qid) => n.delete(qid)); return n; });
    } catch (e) {
      setRemovalError(e?.message || "Failed to remove assignment(s).");
    } finally {
      setRemovingIds(new Set());
      setBulkBusy(false);
    }
  }, [financialYearId, scopeId, onRefreshAssignments]);

  const handleRemoveSelected = () => performRemovals(filteredQIds.filter((qid) => selectedIds.has(qid)));

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1400, display: "flex" }}>
      <style>{`@keyframes qod-spin { to { transform: rotate(360deg); } }`}</style>
      <div onClick={onClose} style={{ flex: 1, background: "rgba(0,0,0,.35)", cursor: "pointer" }} />
      <div style={{ width: 540, maxWidth: "95vw", background: T.white, display: "flex", flexDirection: "column", boxShadow: "-8px 0 40px rgba(0,0,0,.18)" }}>

        <div style={{ padding: "16px 18px 13px", borderBottom: `1px solid ${T.rule}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>
                {scopeLabel} · {mode === "unassigned" ? "Unassigned Questions" : "Assignments"}
              </div>
              <div style={{ fontSize: 11, color: T.ink3, marginTop: 2 }}>
                {filteredQIds.length} of {qIds.length} question{qIds.length !== 1 ? "s" : ""} shown{isReadOnly ? " · Read-only" : ""}
              </div>
            </div>
            <button onClick={onClose} style={{ border: `1px solid ${T.rule}`, borderRadius: 8, background: T.bg, width: 30, height: 30, cursor: "pointer", fontSize: 15, color: T.ink3 }}>×</button>
          </div>

          <div style={{ marginTop: 10 }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search questions…"
              style={{ width: "100%", padding: "7px 10px", fontSize: 12, border: `1px solid ${T.rule}`, borderRadius: 7, background: T.bg, boxSizing: "border-box" }} />
          </div>
        </div>

        {!isReadOnly && removalError && (
          <div style={{ margin: "10px 14px 0", padding: "8px 12px", borderRadius: 7, background: T.redL, border: `1px solid ${T.redB}`, color: T.red, fontSize: 11.5 }}>
            ⚠ {removalError}
          </div>
        )}

        {!isReadOnly && filteredQIds.length > 0 && (
          <div style={{ margin: "10px 14px 0", padding: "7px 11px", borderRadius: 7, background: T.bg, border: `1px solid ${T.rule}`, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={{ accentColor: T.blue, width: 13, height: 13 }} />
              Select all ({filteredQIds.length})
            </label>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={handleRemoveSelected} disabled={selectedIds.size === 0 || bulkBusy}
                style={{ fontSize: 11, fontWeight: 700, color: selectedIds.size ? T.red : T.ink4, background: T.white, border: `1.5px solid ${selectedIds.size ? T.redB : T.rule}`, borderRadius: 7, padding: "5px 10px", cursor: selectedIds.size && !bulkBusy ? "pointer" : "not-allowed" }}>
                Remove Selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
              </button>
            </div>
          </div>
        )}

        <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px" }}>
          {filteredQIds.length === 0 && <div style={{ textAlign: "center", color: T.ink4, fontSize: 12, padding: "30px 0" }}>No questions match your search.</div>}

          {filteredQIds.map((qid, i) => {
            const title = questionTitles[qid] || `Question #${qid}`;

            if (isReadOnly) {
              return (
                <div key={qid} style={{ padding: "11px 13px", borderRadius: 9, border: `1px solid ${T.rule}`, marginBottom: 8, background: i % 2 === 0 ? T.white : T.bg, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, flex: 1 }}>{title}</span>
                  <span style={{ fontSize: 10, color: T.amber, background: T.amberL, padding: "2px 8px", borderRadius: 20, border: `1px solid ${T.amberB}`, whiteSpace: "nowrap" }}>Needs owner</span>
                </div>
              );
            }

            const isRemoving = removingIds.has(qid);
            const isSelected = selectedIds.has(qid);
            return (
              <div key={qid} style={{ padding: "11px 13px", borderRadius: 9, border: `1px solid ${T.rule}`, marginBottom: 8, background: i % 2 === 0 ? T.white : T.bg, display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(qid)} style={{ accentColor: T.blue, width: 13, height: 13, flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, flex: 1 }}>{title}</span>
                <button onClick={() => performRemovals([qid])} disabled={isRemoving || bulkBusy}
                  style={{ border: `1px solid ${T.redB}`, background: T.white, color: T.red, borderRadius: 6, padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: (isRemoving || bulkBusy) ? "not-allowed" : "pointer", flexShrink: 0 }}>
                  {isRemoving ? <Spin size={9} color={T.red} /> : "Remove"}
                </button>
              </div>
            );
          })}
        </div>

        <div style={{ padding: "10px 18px", borderTop: `1px solid ${T.rule}` }}>
          <button onClick={onClose} style={{ width: "100%", padding: "8px 0", borderRadius: 8, border: `1.5px solid ${T.rule}`, background: T.white, color: T.ink2, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default QualitativeOwnerAssignedDrawer;
